// API service for communicating with the backend

import api from "../utils/apiClient"; // Use centralized API client
import { config } from "../config";
import {
  QuestionResponse,
  HealthResponse,
  StatsResponse,
  ConversationHistoryResponse,
  ClearHistoryResponse,
  DeleteConversationResponse,
  SummarizeResponse,
  RenameConversationResponse,
  FeedbackSubmissionResponse,
  MessageFeedback,
} from "../types";
import { pollingService } from "./pollingService";
import { retryService } from "./retryService";

export const apiService = {
  // Ask a question to the knowledge base with thread support
  async askQuestion(
    question: string,
    debug: boolean = false,
    threadId: string = "default"
  ): Promise<QuestionResponse> {
    return retryService
      .retryWithStrategy(async () => {
        const response = await api.post<QuestionResponse>("/chat/ask", {
          question,
          debug,
          thread_id: threadId,
        });
        return response.data;
      }, "all")
      .catch((error) => {
        console.error("Error asking question:", error);
        throw new Error("Failed to get response from the assistant");
      });
  },

  // Ask a question using async polling to avoid timeouts
  async askQuestionAsync(
    question: string,
    debug: boolean = false,
    threadId: string = "default",
    onProgress?: (progress: number) => void,
    onStatusChange?: (status: string) => void,
    image?: File | null
  ): Promise<QuestionResponse> {
    try {
      // If image is provided, use FormData endpoint
      if (image) {
        const formData = new FormData();
        formData.append("question", question);
        formData.append("thread_id", threadId);
        formData.append("debug", debug.toString());
        formData.append("image", image);

        // Use direct API call for FormData (not through polling service)
        const response = await api.post<QuestionResponse>(
          "/chat/ask-with-image",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        return response.data;
      } else {
        // Use existing polling service for text-only questions
        const result = await pollingService.askQuestionWithPolling(
          question,
          threadId,
          debug,
          {
            maxAttempts: 60, // 2 minutes with 2-second intervals
            intervalMs: 2000,
            timeoutMs: 120000,
            onProgress,
            onStatusChange,
          }
        );

        if (!result.result) {
          throw new Error("No result received from polling");
        }

        return result.result as QuestionResponse;
      }
    } catch (error) {
      console.error("Error asking question async:", error);
      throw new Error("Failed to get response from the assistant");
    }
  },

  // Check if the backend is healthy
  async checkHealth(): Promise<HealthResponse> {
    try {
      const response = await api.get<HealthResponse>("/health/");
      return response.data;
    } catch (error) {
      console.error("Error checking health:", error);
      throw new Error("Backend is not available");
    }
  },

  // Get knowledge base statistics
  async getStats(): Promise<StatsResponse> {
    try {
      const response = await api.get<StatsResponse>("/health/stats");
      return response.data;
    } catch (error) {
      console.error("Error getting stats:", error);
      throw new Error("Failed to get statistics");
    }
  },

  // Load sample data (for testing)
  async loadSampleData(): Promise<{ message: string; success: boolean }> {
    try {
      const response = await api.post("/load-data");
      return response.data;
    } catch (error) {
      console.error("Error loading sample data:", error);
      throw new Error("Failed to load sample data");
    }
  },

  // Get conversation history for a thread
  async getConversationHistory(
    threadId: string
  ): Promise<ConversationHistoryResponse> {
    try {
      const response = await api.post<ConversationHistoryResponse>(
        "/chat/history",
        {
          thread_id: threadId,
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error getting conversation history:", error);
      throw new Error("Failed to get conversation history");
    }
  },

  // Clear conversation history for a thread
  async clearConversationHistory(
    threadId: string
  ): Promise<ClearHistoryResponse> {
    try {
      const response = await api.post<ClearHistoryResponse>(
        "/chat/clear-history",
        {
          thread_id: threadId,
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error clearing conversation history:", error);
      throw new Error("Failed to clear conversation history");
    }
  },

  // Delete a conversation thread completely
  async deleteConversation(
    threadId: string
  ): Promise<DeleteConversationResponse> {
    try {
      const response = await api.post<DeleteConversationResponse>(
        "/chat/delete-conversation",
        {
          thread_id: threadId,
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting conversation:", error);
      throw new Error("Failed to delete conversation");
    }
  },

  // Get a summary for a conversation
  async summarize(content: string): Promise<SummarizeResponse> {
    try {
      const response = await api.post<SummarizeResponse>("/chat/summarize", {
        content,
      });
      return response.data;
    } catch (error) {
      console.error("Error getting summary:", error);
      throw new Error("Failed to get summary");
    }
  },

  // Get all active conversation threads
  async getActiveThreads(): Promise<{
    threads: Array<{
      thread_id: string;
      message_count: number;
      last_message_time: string | null;
      name: string;
    }>;
    total_threads: number;
  }> {
    try {
      const response = await api.get("/chat/threads");
      return response.data;
    } catch (error) {
      console.error("Error getting active threads:", error);
      throw new Error("Failed to get active threads");
    }
  },

  // Rename a conversation thread
  async renameConversation(
    threadId: string,
    newTitle: string
  ): Promise<RenameConversationResponse> {
    try {
      const response = await api.post<RenameConversationResponse>(
        "/chat/rename-conversation",
        {
          thread_id: threadId,
          new_title: newTitle,
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error renaming conversation:", error);
      throw new Error("Failed to rename conversation");
    }
  },

  // Submit feedback for a message
  async submitFeedback(
    messageId: string,
    threadId: string,
    feedbackType: "thumbs_up" | "thumbs_down",
    rating?: number,
    comment?: string
  ): Promise<FeedbackSubmissionResponse> {
    try {
      const response = await api.post<FeedbackSubmissionResponse>(
        "/feedback/submit",
        {
          message_id: messageId,
          thread_id: threadId,
          feedback_type: feedbackType,
          rating,
          comment,
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error submitting feedback:", error);
      throw new Error("Failed to submit feedback");
    }
  },

  // Get existing feedback for a message
  async getMessageFeedback(messageId: string): Promise<MessageFeedback | null> {
    try {
      const response = await api.get<MessageFeedback | { has_feedback: false }>(
        `/feedback/message/${messageId}`
      );

      if ("has_feedback" in response.data && !response.data.has_feedback) {
        return null;
      }

      return response.data as MessageFeedback;
    } catch (error) {
      console.error("Error getting message feedback:", error);
      return null;
    }
  },

  // Get feedback statistics (admin only)
  async getFeedbackStats(
    startDate?: string,
    endDate?: string,
    threadId?: string
  ): Promise<any> {
    try {
      const response = await api.post("/feedback/stats", {
        start_date: startDate,
        end_date: endDate,
        thread_id: threadId,
      });
      return response.data;
    } catch (error) {
      console.error("Error getting feedback stats:", error);
      throw new Error("Failed to get feedback statistics");
    }
  },

  // Get feedback analytics (admin only)
  async getFeedbackAnalytics(daysBack: number = 30): Promise<any> {
    try {
      const response = await api.get(
        `/feedback/analytics?days_back=${daysBack}`
      );
      return response.data;
    } catch (error) {
      console.error("Error getting feedback analytics:", error);
      throw new Error("Failed to get feedback analytics");
    }
  },

  // Get image URL for display
  getImageUrl(threadId: string, filename: string): string {
    const baseURL = config.API_URL || "";
    return `${baseURL}/files/images/${threadId}/${filename}`;
  },
};

export default apiService;
